// Hook odpowiedzialny za pobranie profilu

import { axios } from 'api';
import { useQuery, UseQueryOptions } from 'react-query';
import { ICommandResponseError as IError, IUserAgreementListItem } from 'api/types';

export type IUserRoles =
  | 'ROLE_USER_MAIN'
  | 'ROLE_USER'
  | 'ROLE_TRADER'
  | 'ROLE_TRADER_MANAGER'
  | 'ROLE_ADMIN'
  | 'ROLE_OPEN_PROFILE';

// Efektywny limit budzetowy (suma: user / department / location).
// Pelne dane z backendu (EffectiveLimit::toArray) - kwota / wydane / pozostale +
// zrodlo (z czego wynika obowiazujacy limit) i okres rozliczeniowy.
export interface IEffectiveLimit {
  amount: number | null;
  spent: number;
  remaining: number | null;
  // 'none' = brak limitu, 'user'/'department'/'location' = z jednego zrodla,
  // 'min' = co najmniej dwa zrodla aktywne, bierzemy najmniejszy remaining
  source: 'none' | 'user' | 'department' | 'location' | 'min';
  // 'd' = dzien, 'w' = tydzien, 'm' = miesiac, 'y' = rok
  period: string | null;
  breakdown: Record<string, { amount: number; spent: number; remaining: number; period: string }>;
}

// Ustawienia CompanyUser zwracane przez CompanyUserPermissionsProvider w module Wise/Company.
// Sterują widocznością modułów w sidebarze panelu klienta.
export interface ICompanyUserSettings {
  has_company_config: boolean;
  is_main_user: boolean;
  company_role: 'manager' | 'buyer' | 'viewer' | 'admin' | null;
  is_available: boolean;
  permissions: {
    documents: boolean;
    payments: boolean;
    offers: boolean;
    returns: boolean;
  };
  has_user_limit: boolean;
  show_limit: boolean;
  has_effective_limit: boolean;
  effective_limit: IEffectiveLimit | null;
  can_place_orders: boolean;
  can_manage_department: boolean;
  assignment: {
    location_id: number | null;
    department_id: number | null;
  } | null;
}

// typ zwracanych danych
export interface IResponse {
  id: number;
  change_password: boolean;
  consents_required: boolean;
  first_name: string;
  last_name: string;
  email: string;
  overloged: false;
  logged_user: {
    first_name: string;
    id: number;
    last_name: string;
    role: string;
  };
  role: IUserRoles;
  sale_supervisor: {
    name: string;
    phone: string;
    email: string;
  };
  agreements: IUserAgreementListItem[];
  customer: {
    id: number;
    name: string;
    address: {
      street: string;
      house_number: string;
      postal_code: string;
      city: string;
      country: string;
    };
    nip: string;
    email: string;
    phone?: string;
  };
  additional_information?: {
    additional_information?: Record<string, { text: string; url: string }>;
    company_user_settings?: ICompanyUserSettings;
  };
}

const getUserProfile = (): Promise<IResponse> => axios.get('/users/profile');

export const useGetUserProfile = (options?: UseQueryOptions<IResponse, IError>) =>
  useQuery<IResponse, IError>('profile', getUserProfile, options);
